
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Users, Shield, Ban, CheckCircle, XCircle } from 'lucide-react';
import { Database } from '@/integrations/supabase/types';
import CreateUserForm from './CreateUserForm';

type Profile = Database['public']['Tables']['profiles']['Row'];

export default function UserManagement() {
  const [users, setUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: "Error",
        description: "Failed to fetch users",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId: string, newRole: 'admin' | 'user') => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId);

      if (error) throw error;

      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));

      toast({
        title: "Success",
        description: `User role updated to ${newRole}`,
      });
    } catch (error) {
      console.error('Error updating user role:', error);
      toast({
        title: "Error",
        description: "Failed to update user role",
        variant: "destructive",
      });
    }
  };

  const toggleUserBan = async (userId: string, currentBanStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_banned: !currentBanStatus })
        .eq('id', userId);

      if (error) throw error;

      setUsers(users.map(user => 
        user.id === userId ? { ...user, is_banned: !currentBanStatus } : user
      ));

      toast({
        title: "Success",
        description: `User ${!currentBanStatus ? 'banned' : 'unbanned'} successfully`,
      });
    } catch (error) {
      console.error('Error updating user ban status:', error);
      toast({
        title: "Error",
        description: "Failed to update user status",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-purple-600" />
              <span>User Management</span>
            </CardTitle>
            <CardDescription>
              Manage user accounts, roles, and permissions
            </CardDescription>
          </div>
          <CreateUserForm onUserCreated={fetchUsers} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {users.map((user) => (
            <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <div>
                    <h4 className="font-medium">{user.full_name || 'No name'}</h4>
                    <p className="text-sm text-gray-600">{user.email}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                      {user.role}
                    </Badge>
                    <Badge variant={user.email_verified ? 'outline' : 'destructive'}>
                      {user.email_verified ? (
                        <><CheckCircle className="h-3 w-3 mr-1" />Verified</>
                      ) : (
                        <><XCircle className="h-3 w-3 mr-1" />Unverified</>
                      )}
                    </Badge>
                    {user.is_banned && (
                      <Badge variant="destructive">
                        <Ban className="h-3 w-3 mr-1" />Banned
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="mt-2 text-sm text-gray-500">
                  API Calls: {user.api_calls_used} | Storage: {((user.monthly_space_used || 0) / 1024 / 1024).toFixed(1)} MB
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateUserRole(user.id, user.role === 'admin' ? 'user' : 'admin')}
                  className="flex items-center space-x-1"
                >
                  <Shield className="h-3 w-3" />
                  <span>{user.role === 'admin' ? 'Remove Admin' : 'Make Admin'}</span>
                </Button>
                <Button
                  variant={user.is_banned ? "default" : "destructive"}
                  size="sm"
                  onClick={() => toggleUserBan(user.id, user.is_banned)}
                  className="flex items-center space-x-1"
                >
                  {user.is_banned ? (
                    <><CheckCircle className="h-3 w-3" />Unban</>
                  ) : (
                    <><Ban className="h-3 w-3" />Ban</>
                  )}
                </Button>
              </div>
            </div>
          ))}
          {users.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No users found
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
